import { useEffect, useRef, useState } from "react";
import { getCustomisationValue, getGroupData } from "../../EditablePreview";
import InlineProgressBar, { convertCustomisationDataToBarSettings } from "../progress-bar/inline-progress-bar";
import VisualisedNode, { IMAGE_MODES } from "../VisualisedNode";
import './F2u.scss';
import { PROGRESS_BAR_STATE } from "../generic-progress-bar/PositionableProgressBar";

// Animation cycle tick counts (each tick = 100ms)
const APPEAR_TICKS = 5;    // 500ms appear
const SHOW_TICKS = 20;     // 2000ms fully visible
const DISAPPEAR_TICKS = 5; // 500ms disappear
const HIDDEN_TICKS = 10;   // 1000ms hidden (cycling preview only)
const TOTAL_ANIM_TICKS = APPEAR_TICKS + SHOW_TICKS + DISAPPEAR_TICKS + HIDDEN_TICKS;
const ANIM_END = APPEAR_TICKS + SHOW_TICKS + DISAPPEAR_TICKS; // end of one-shot animation

const easeBackOut = (t) => {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
};

const getRewardAnimStyle = (tick, appearTween, disappearTween, previewScale) => {
    // Values may be stored as strings after user edits via the dropdown, so coerce to number
    const appear = Number(appearTween ?? 0);
    const disappear = Number(disappearTween ?? 0);

    const cyclePhase = tick % TOTAL_ANIM_TICKS;
    const isHidden = cyclePhase >= APPEAR_TICKS + SHOW_TICKS + DISAPPEAR_TICKS;
    if (isHidden) return { opacity: 0, pointerEvents: 'none' };
    const isAppearing = cyclePhase < APPEAR_TICKS;
    const isDisappearing = cyclePhase >= APPEAR_TICKS + SHOW_TICKS && cyclePhase < APPEAR_TICKS + SHOW_TICKS + DISAPPEAR_TICKS;
    if (isAppearing) {
        const p = cyclePhase / APPEAR_TICKS;
        switch (appear) {
            case 0: return {}; // Instant
            case 1: return { transform: `scale(${easeBackOut(p)})` }; // Pop
            case 2: return { transform: `scale(${easeBackOut(p)}) rotate(${(1 - p) * 15}deg)` }; // Pop Rotate
            case 3: return { opacity: p }; // Fade
            case 4: return { opacity: p, transform: `translateY(${(1 - p) * -40 * previewScale}px)` }; // Jump Fade
            case 5: return { opacity: p, transform: `translateY(${(1 - p) * -40 * previewScale}px)` }; // Bounce Fade
            default: return { opacity: p };
        }
    }
    if (isDisappearing) {
        const p = (cyclePhase - APPEAR_TICKS - SHOW_TICKS) / DISAPPEAR_TICKS;
        switch (disappear) {
            case 0: return { opacity: 0 }; // Instant
            case 1: return { transform: `scale(${1 - p})` }; // Pop
            case 2: return { transform: `scale(${1 - p}) rotate(${-p * 10}deg)` }; // Pop Rotate
            case 3: return { opacity: 1 - p }; // Fade
            case 4: return { opacity: 1 - p, transform: `translateY(${p * 40 * previewScale}px)` }; // Jump Fade
            case 5: return { opacity: 1 - p, transform: `translateY(${p * -40 * previewScale}px)` }; // Bounce Fade
            default: return { opacity: 1 - p };
        }
    }
    return {}; // Fully visible
};

const F2uReward = (props) => {
    const node = props.node;
    const isBarAnimMode = props.visualisedNode !== props.node;

    // tick drives periodic re-renders (reads PROGRESS_BAR_STATE) and the cycling
    // preview animation in direct-edit mode.
    const [tick, setTick] = useState(0);
    useEffect(() => {
        const interval = setInterval(() => setTick(t => t + 1), 100);
        return () => clearInterval(interval);
    }, []);

    // One-shot animation for bar-anim mode.
    // animTick: -1 = hidden, 0..ANIM_END-1 = playing
    const [animTick, setAnimTick] = useState(-1);
    const [animatingIndex, setAnimatingIndex] = useState(-1);
    const lastTriggeredRef = useRef(-1);
    const animIntervalRef = useRef(null);
    const [rewardImgDimensions, setRewardImgDimensions] = useState({ width: '100px', height: '100px' });

    useEffect(() => {
        return () => { if (animIntervalRef.current) clearInterval(animIntervalRef.current); };
    }, []);

    // Compute which reward point the bar is currently at (or -1 if between points)
    let rewardNode = node.subNodes[1];
    let dataIndex = 0;

    if (isBarAnimMode) {
        dataIndex = -1;
        const rewardNodeCustomisationVarsArray = getGroupData(props.node.customisationVars, rewardNode.config.groupSettings.dataLink, -1);
        const currentBarPerc = PROGRESS_BAR_STATE.perc * 100;

        // When bar returns to start, allow all rewards to trigger again
        if (PROGRESS_BAR_STATE.perc < 0.05) {
            lastTriggeredRef.current = -1;
        }

        for (let i = 0; i < rewardNodeCustomisationVarsArray.length; i++) {
            const rewardData = rewardNodeCustomisationVarsArray[i];
            if ((rewardData?.completePerc >= currentBarPerc - 1 && rewardData?.completePerc <= currentBarPerc + 1) ||
                (rewardData?.completePerc === 100 && currentBarPerc >= 100)) {
                dataIndex = i;
            }
        }
        // No fallback — stay -1 when not at a reward point
    } else if (props.activeNode?.parent === rewardNode) {
        dataIndex = props.activeNode.dataIndex ?? 0;
    }

    // Trigger one-shot animation when a new reward point is reached in bar-anim mode
    useEffect(() => {
        if (!isBarAnimMode) return;
        if (dataIndex !== -1 && dataIndex !== lastTriggeredRef.current) {
            lastTriggeredRef.current = dataIndex;
            if (animIntervalRef.current) clearInterval(animIntervalRef.current);
            setAnimatingIndex(dataIndex);
            setAnimTick(0);
            let t = 0;
            animIntervalRef.current = setInterval(() => {
                t++;
                if (t >= ANIM_END) {
                    clearInterval(animIntervalRef.current);
                    animIntervalRef.current = null;
                    setAnimTick(-1);
                } else {
                    setAnimTick(t);
                }
            }, 100);
        }
    }, [dataIndex, isBarAnimMode]);

    // In bar-anim mode: show the animating reward (or nothing if hidden).
    // In direct-edit mode: always show the selected reward with cycling animation.
    const renderIndex = isBarAnimMode ? (animTick === -1 ? -1 : animatingIndex) : dataIndex;
    const effectiveTick = isBarAnimMode ? (animTick < 0 ? 0 : animTick) : tick;

    let textSettingsNode = node.subNodes[0];
    const textSettingCustomisationVars = getGroupData(props.node.customisationVars, textSettingsNode.config.groupSettings.dataLink, 0);
    const textSettingsStyle = { margin: 0, padding: 0, whiteSpace: 'nowrap', textAlign: 'center' };
    if (textSettingCustomisationVars.showShadow) {
        textSettingsStyle.textShadow = `${textSettingCustomisationVars.shadowThickness}px ${textSettingCustomisationVars.shadowThickness}px ${textSettingCustomisationVars.shadowBlur}px ${textSettingCustomisationVars.shadowColour}`;
    }
    if (textSettingCustomisationVars.addOutline) {
        textSettingsStyle['-webkit-text-stroke'] = `${textSettingCustomisationVars.outlineThickness}px ${textSettingCustomisationVars.outlineColour}`;
    }

    const rewardImgLoaded = (e) => {
        const img = e.target;
        setRewardImgDimensions({
            width: `${img.naturalWidth * props.previewScale}px`,
            height: `${img.naturalHeight * props.previewScale}px`
        });
    };

    // Nothing to show — render an invisible placeholder to keep layout stable
    if (renderIndex === -1) {
        return <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }} />;
    }

    const rewardNodeCustomisationVars = getGroupData(props.node.customisationVars, rewardNode.config.groupSettings.dataLink, renderIndex);
    const rewardText = getCustomisationValue(rewardNode.customisationVars, rewardNode.config.children[0].fields[0].customFieldConfig.dataLink, renderIndex, null) || "Reward";

    const animStyle = getRewardAnimStyle(
        effectiveTick,
        textSettingCustomisationVars?.appearTween ?? 0,
        textSettingCustomisationVars?.disappearTween ?? 0,
        props.previewScale ?? 1
    );

    return (
        <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}>
            <div style={{ width: '100%', flexDirection: 'row', display: 'flex', alignItems: 'center', justifyContent: 'center', ...animStyle }}>
                <VisualisedNode {...props} node={rewardNode} imageMode={IMAGE_MODES.naturalSize}>
                    {rewardNodeCustomisationVars.showGoalImage &&
                        <div style={{ minWidth: 'fit-content', width: '100%', flexDirection: 'row', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img src={rewardNodeCustomisationVars.goalImageFilename.srcUrl} alt="F2U Reward" style={{ width: rewardImgDimensions.width, height: rewardImgDimensions.height }} onLoad={rewardImgLoaded} />
                        </div>
                    }
                    {rewardNodeCustomisationVars.showGoalTitle &&
                        <div style={{ minWidth: 'fit-content', width: '100%', flexDirection: 'row', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <VisualisedNode {...props} node={textSettingsNode}>
                                <p style={textSettingsStyle}>{rewardText}</p>
                            </VisualisedNode>
                        </div>
                    }
                </VisualisedNode>
            </div>
        </div>
    );
}

export default F2uReward;
